/**
 * Load image through backend proxy to bypass CORS.
 * Falls back to direct fetch if proxy returns 403 (e.g. proxy requires staff login).
 */
export async function loadImageAsBlobUrl(originalUrl: string): Promise<string> {
  const proxiedUrl = `/ironview360/proxy-signed-image?url=${encodeURIComponent(originalUrl)}`;
  let response = await fetch(proxiedUrl);
  if (!response.ok) {
    // Proxy may return 403 for non-staff users (@staff_only decorator)
    if (response.status === 403 || response.status === 401) {
      response = await fetch(originalUrl);
    }
    if (!response.ok) throw new Error('Failed to fetch image blob');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Load image as HTMLImageElement
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Downloads the layout with markers overlayed as a PNG
 * Uses backend proxy to bypass CORS issues with signed GCS URLs
 * Supports both image and PDF layouts
 * 
 * @param svgRef Reference to the SVG element containing the rendered layout
 * @param fileName Name for the downloaded file (without extension)
 */
export const downloadLayoutImage = async (
  svgRef: React.RefObject<SVGSVGElement>,
  fileName: string = 'layout-with-markers'
): Promise<void> => {
  if (!svgRef.current) {
    throw new Error('SVG reference not available');
  }

  try {
    const svg = svgRef.current;
    const viewBox = svg.viewBox.baseVal;
    
    // Check if this is a PDF layout by looking for a stored canvas reference
    const pdfCanvas = (svg as any).__pdfCanvas as HTMLCanvasElement | undefined;
    const isPdfLayout = !!pdfCanvas;
    
    let width: number;
    let height: number;
    let backgroundContent: HTMLImageElement | HTMLCanvasElement;

    if (isPdfLayout && pdfCanvas) {
      // PDF Layout: Use the canvas reference stored on the SVG element
      backgroundContent = pdfCanvas;
      
      // Get dimensions from the canvas
      width = pdfCanvas.width;
      height = pdfCanvas.height;
    } else {
      // Image Layout: Get image element
      const imageElement = svg.querySelector('image');
      if (!imageElement) {
        throw new Error('No image found in SVG');
      }
      
      const originalImageUrl = imageElement.getAttribute('href') || imageElement.getAttribute('xlink:href');
      if (!originalImageUrl) {
        throw new Error('No image URL found');
      }

      // Get the actual image dimensions (without viewBox padding)
      const imgWidth = imageElement.getAttribute('width');
      const imgHeight = imageElement.getAttribute('height');
      width = imgWidth ? parseFloat(imgWidth) : viewBox.width;
      height = imgHeight ? parseFloat(imgHeight) : viewBox.height;

      // Load background image through proxy
      const blobUrl = await loadImageAsBlobUrl(originalImageUrl);
      backgroundContent = await loadImage(blobUrl);
      URL.revokeObjectURL(blobUrl);
    }

    // Create canvas with 2x resolution for better quality
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.scale(scale, scale);

    // Fill white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    // Draw the background (image or PDF canvas)
    ctx.drawImage(backgroundContent, 0, 0, width, height);

    // Get the group element (contains the markers and area tags)
    const group = svg.querySelector('g');
    if (!group) {
      throw new Error('SVG group element not found');
    }

    // Store original transform
    const originalTransform = group.getAttribute('transform');
    
    // Temporarily reset transform to capture the base view
    group.setAttribute('transform', 'translate(0, 0) scale(1)');

    // Wait a frame for the transform to apply
    await new Promise(resolve => requestAnimationFrame(resolve));

    try {
      // Create a temporary SVG container for markers and area tags
      const markerContainer = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      markerContainer.setAttribute('width', width.toString());
      markerContainer.setAttribute('height', height.toString());
      markerContainer.setAttribute('viewBox', `0 0 ${width} ${height}`);

      // Clone all children from the group except the image element
      // This preserves the structure and styling
      Array.from(group.children).forEach((child) => {
        // Skip the image element (for image layouts)
        if (child.tagName.toLowerCase() === 'image') {
          return;
        }
        
        const cloned = child.cloneNode(true) as Element;
        
        // Remove delete buttons from photo markers
        const deleteButtons = cloned.querySelectorAll('.delete-button');
        deleteButtons.forEach(btn => btn.remove());
        
        markerContainer.appendChild(cloned);
      });

      // Convert markers SVG to image
      const svgString = new XMLSerializer().serializeToString(markerContainer);
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      // Draw markers on canvas
      const markersImg = await loadImage(svgUrl);
      ctx.drawImage(markersImg, 0, 0, width, height);
      URL.revokeObjectURL(svgUrl);
    } finally {
      // Restore original transform
      if (originalTransform) {
        group.setAttribute('transform', originalTransform);
      }
    }

    // Convert canvas to blob and trigger download
    canvas.toBlob(blob => {
      if (!blob) {
        throw new Error('Failed to create blob from canvas');
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.png`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');

  } catch (error) {
    console.error('Error downloading layout image:', error);
    throw error;
  }
};
